/* EARTHLING — pixel-local iridescent logo, stable viewport-anchor revision.
 * Homepage, ertheX and FRE3.0 product shops.
 *
 * CSS does all pixel-local contrast. The fixed mask never follows scroll,
 * native overscroll, mobile toolbar height, or ancestor transition frames.
 * Coordinates are written as top/left, never as a translated render layer.
 * Geometry is recalculated only for actual layout/width/font changes.
 * Class/style changes update visibility, NOT the mask's position.
 */
(function () {
  'use strict';

  var key = '__earthlingPixelLogoContrast';
  if (window[key]) {
    window[key].refresh();
    return;
  }
  if (!window.CSS || !CSS.supports ||
      !CSS.supports('mix-blend-mode', 'difference') ||
      !(CSS.supports('mask-image', 'linear-gradient(#000, #000)') ||
        CSS.supports('-webkit-mask-image', 'linear-gradient(#000, #000)'))) return;

  var selector = '.earthling-iridescent-logo--pixel';
  var logo = null;
  var bar = null;
  var paint = null;
  var frame = 0;
  var generation = 0;
  var needsLayout = false;
  var lastBox = '';
  var lastOpacity = '';
  var lastVisibility = '';
  var resizeObserver = null;
  var attributeObserver = null;
  var parents = [];
  var removers = [];
  var destroyed = false;
  var viewportWidth = window.innerWidth;
  var pixelRatio = window.devicePixelRatio;

  function listen(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    removers.push(function () { target.removeEventListener(type, handler, options); });
  }

  function clearMark() {
    generation += 1;
    if (frame) window.cancelAnimationFrame(frame);
    frame = 0;
    needsLayout = false;
    if (resizeObserver) resizeObserver.disconnect();
    if (attributeObserver) attributeObserver.disconnect();
    resizeObserver = null;
    attributeObserver = null;
    if (logo) logo.removeAttribute('data-pixel-contrast');
    if (paint && paint.parentNode) paint.parentNode.removeChild(paint);
    logo = null;
    bar = null;
    paint = null;
    parents = [];
    lastBox = '';
    lastOpacity = '';
    lastVisibility = '';
  }

  function requestSync(layout) {
    needsLayout = needsLayout || layout === true;
    if (!destroyed && !frame && logo && paint) {
      frame = window.requestAnimationFrame(sync);
    }
  }

  function requestLayout() { requestSync(true); }
  function requestVisibility() { requestSync(false); }

  function sync() {
    frame = 0;
    if (destroyed || !logo || !paint) return;
    if (!document.documentElement.contains(logo)) {
      refresh();
      return;
    }

    var layout = needsLayout;
    needsLayout = false;
    var opacity = 1;
    var hidden = false;
    // Ancestor class changes can hide a header or open a drawer. They must
    // not resample a temporarily displaced viewport during an edge gesture.
    for (var i = 0; i < parents.length && !hidden; i += 1) {
      var style = window.getComputedStyle(parents[i]);
      var parentOpacity = parseFloat(style.opacity);
      opacity *= isNaN(parentOpacity) ? 1 : parentOpacity;
      hidden = style.display === 'none' || style.visibility === 'hidden' ||
        style.visibility === 'collapse' || parents[i].hidden || opacity === 0;
    }

    if (!hidden && (layout || !lastBox)) {
      var rect = logo.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        var left = rect.left;
        var top = rect.top;
        if (bar) {
          var barRect = bar.getBoundingClientRect();
          var barStyle = window.getComputedStyle(bar);
          var fixedLeft = parseFloat(barStyle.left);
          var fixedTop = parseFloat(barStyle.top);
          // Read the logo's LOCAL offset inside the fixed bar. Subtracting
          // the bar rectangle cancels viewport/page displacement, including
          // any transient edge-scroll offset. The bar itself is at 0,0.
          left = rect.left - barRect.left + (isNaN(fixedLeft) ? 0 : fixedLeft);
          top = rect.top - barRect.top + (isNaN(fixedTop) ? 0 : fixedTop);
        }
        var box = [left, top, rect.width, rect.height].join(',');
        if (box !== lastBox) {
          paint.style.left = left + 'px';
          paint.style.top = top + 'px';
          paint.style.width = rect.width + 'px';
          paint.style.height = rect.height + 'px';
          lastBox = box;
        }
      } else hidden = true;
    }
    hidden = hidden || !lastBox;
    var visibility = hidden ? 'hidden' : 'visible';
    var nextOpacity = String(opacity);
    if (nextOpacity !== lastOpacity) {
      paint.style.opacity = nextOpacity;
      lastOpacity = nextOpacity;
    }
    if (visibility !== lastVisibility) {
      paint.style.visibility = visibility;
      lastVisibility = visibility;
    }
    if (!hidden && logo.getAttribute('data-pixel-contrast') !== 'ready') {
      logo.setAttribute('data-pixel-contrast', 'ready');
    }
  }

  function refresh() {
    if (destroyed) return;
    var next = document.querySelector(selector);
    if (next === logo) {
      requestLayout();
      return;
    }
    clearMark();
    if (!next || !document.body) return;
    var maskURL = next.getAttribute('data-contrast-mask');
    if (!maskURL) return;
    logo = next;
    bar = logo.closest('.earthling-header-bar');
    var ticket = generation;
    var mask = new Image();
    mask.onload = function () {
      if (destroyed || ticket !== generation || logo !== next ||
          !document.documentElement.contains(next)) return;
      paint = document.createElement('span');
      paint.className = 'earthling-pixel-iridescent-logo';
      paint.setAttribute('aria-hidden', 'true');
      paint.setAttribute('role', 'presentation');
      // No isolated wrapper: the mark blends with actual page pixels.
      document.body.appendChild(paint);
      var node = logo;
      while (node) {
        parents.push(node);
        node = node.parentElement;
      }
      if (window.ResizeObserver) {
        resizeObserver = new ResizeObserver(requestLayout);
        resizeObserver.observe(logo);
        if (bar) resizeObserver.observe(bar);
      }
      if (window.MutationObserver) {
        attributeObserver = new MutationObserver(requestVisibility);
        parents.forEach(function (parent) {
          attributeObserver.observe(parent, {
            attributes: true,
            attributeFilter: ['class', 'style', 'hidden']
          });
        });
      }
      needsLayout = true;
      sync();
    };
    mask.onerror = function () {
      // Leave the page-specific fallback visible if the mask cannot load.
    };
    mask.src = maskURL;
  }

  function viewportResize() {
    var width = window.innerWidth;
    var ratio = window.devicePixelRatio;
    // Mobile address-bar collapse/expansion changes height on the first
    // down/up gesture. That is not a layout change for a top-fixed mark.
    if (width === viewportWidth && ratio === pixelRatio) return;
    viewportWidth = width;
    pixelRatio = ratio;
    requestLayout();
  }

  window[key] = {
    refresh: refresh,
    destroy: function () {
      destroyed = true;
      clearMark();
      removers.forEach(function (remove) { remove(); });
      removers = [];
      delete window[key];
    }
  };

  // Deliberately no scroll/wheel/touchmove, visualViewport offset tracking,
  // or transition-following loop. Native CSS owns scrolling and blending.
  listen(window, 'resize', viewportResize, { passive: true });
  listen(window, 'orientationchange', requestLayout, { passive: true });
  listen(window, 'pageshow', refresh);
  listen(window, 'load', requestLayout);
  listen(document, 'shopify:section:load', refresh);
  listen(document, 'shopify:section:reorder', requestLayout);
  listen(document, 'shopify:section:unload', function (event) {
    if (logo && event.target instanceof Element && event.target.contains(logo)) clearMark();
  });
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(requestLayout);
  if (document.readyState === 'loading') listen(document, 'DOMContentLoaded', refresh);
  else refresh();
})();
